import React from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  width: auto;
  max-width: 600px;
  min-width: 450px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: #6B7280;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.25rem;
  
  &:hover {
    background-color: #F3F4F6;
    color: #374151;
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
  margin-right: 2rem;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #ECECEC;
  margin: 0.5rem 0;
  width: 100%;
`;

const Section = styled.div`
  margin-bottom: 0.75rem;
`;

const SectionTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const SectionContent = styled.div`
  font-size: 0.875rem;
  color: #6B7280;
  line-height: 1.4;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Tag = styled.span`
  color: #6F6F6F;
  font-size: 16px;
  font-weight: 400;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const ParameterSection = styled.div`
  margin-bottom: 0.5rem;
`;

const ParameterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const ParameterHeader = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  padding: 0.5rem;
  text-align: center;
`;

const ParameterRow = styled.div`
  display: contents;
`;

const ParameterCell = styled.div`
  font-size: 0.875rem;
  color: #6B7280;
  padding: 0.25rem;
  text-align: center;
`;

const SaveButton = styled.button`
  background-color: #00B5B8;
  color: #FFFFFF;
  border: none;
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  width: fit-content;
  margin-top: 0.5rem;

  &:hover {
    background-color: #0891B2;
  }
`;

const SubTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.7rem;
  margin: 0;
`;

const SubTableRow = styled.tr`
  border-bottom: 1px solid #E5E7EB;

  &:last-child {
    border-bottom: none;
  }
`;

const SubTableCell = styled.td`
  padding: 0.4rem 0.3rem;
  font-size: 0.7rem;
  color: #374151;
  text-align: center;
  vertical-align: top;
`;

const SubTableHeaderCell = styled.th`
  padding: 0.4rem 0.3rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: #6B7280;
  text-align: center;
  background-color: #F9FAFB;
`;

// ฟังก์ชันตรวจสอบว่าพารามิเตอร์เป็นชนิด radio หรือไม่
const isRadioParameter = (parameterName) => {
  const radioParams = [
    'Protein', 'Glucose', 'Ketones', 'Blood', 'Leukocytes', 'Nitrite', 'Bilirubin', 'Urobilinogen',
    'Color', 'Clarity',
    'Color (Stool)', 'Appearance (Stool)', 'Parasite',
    'RBC Index',
    'HBsAb', 'HBsAg', 'HCV Ab', 'Hemoglobin detection stool'
  ];
  return radioParams.includes(parameterName);
};

const isCountDropdownParameter = (parameterName) => {
  return ['WBC (Stool)', 'RBC (Stool)'].includes(parameterName);
};

const TemplateDetailModal = ({ isOpen, onClose, templateData }) => {
  if (!isOpen || !templateData) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>
        
        <ModalTitle>Template</ModalTitle>
        <Divider />
        
        <Section>
          <SectionTitle>ชื่อ Template</SectionTitle>
          <SectionContent>{templateData.templateName}</SectionContent>
        </Section>

        <Section>
          <SectionTitle>ชื่อผู้สร้าง</SectionTitle>
          <SectionContent>{templateData.createdBy}</SectionContent>
        </Section>

        <Section>
          <SectionTitle>ป้ายกำกับ (Tag)</SectionTitle>
          <TagsContainer>
            {templateData.tags?.map((tag, index) => (
              <Tag key={index}>{tag}</Tag>
            ))}
          </TagsContainer>
        </Section>

        <TwoColumnGrid>
          <Section>
            <SectionTitle>สำหรับเพศ</SectionTitle>
            <SectionContent>{templateData.gender}</SectionContent>
          </Section>

          <Section>
            <SectionTitle>ช่วงอายุ</SectionTitle>
            <SectionContent>
              {Array.isArray(templateData.ageRange)
                ? `${templateData.ageRange[0]}-${templateData.ageRange[1]} ปี`
                : `${templateData.ageRange} ปี`}
            </SectionContent>
          </Section>
        </TwoColumnGrid>

        <Divider />

        <ParameterSection>
          <ParameterGrid>
            <ParameterHeader>พารามิเตอร์</ParameterHeader>
            <ParameterHeader>ค่าปกติ</ParameterHeader>
            <ParameterHeader>ค่าที่กำหนด</ParameterHeader>

            {/* รองรับทั้ง structure ใหม่ (selectedParameters) และเก่า (parameters) */}
            {(templateData.selectedParameters || templateData.parameters)?.map((param, index) => {
              // Structure ใหม่: param เป็น object {name, allNormalRanges, ...}
              // Structure เก่า: param เป็น string (ชื่อพารามิเตอร์)
              const paramName = typeof param === 'string' ? param : param.name;
              const allNormalRanges = (typeof param === 'object' ? param.allNormalRanges : null) || [];
              const normalValue = typeof param === 'object' ? param.normalValue : (templateData.normalValues?.[index] || '-');
              const setValue = typeof param === 'object'
                ? (isRadioParameter(paramName)
                  ? templateData.parameterRadioValues?.[paramName]
                  : isCountDropdownParameter(paramName)
                    ? `${templateData.parameterRanges?.[paramName]?.[0] || '-'}-${templateData.parameterRanges?.[paramName]?.[1] || '-'}`
                  : `${templateData.parameterRanges?.[paramName]?.[0] || '-'}-${templateData.parameterRanges?.[paramName]?.[1] || '-'}`)
                : (templateData.setValues?.[index] || '-');

              return (
                <ParameterRow key={index}>
                  <ParameterCell>{paramName}</ParameterCell>
                  <ParameterCell style={{ padding: 0 }}>
                    {allNormalRanges.length > 0 ? (
                      <div style={{ padding: '0.5rem' }}>
                        <SubTable>
                          <thead>
                            <tr>
                              <SubTableHeaderCell>เพศ</SubTableHeaderCell>
                              <SubTableHeaderCell>อายุ</SubTableHeaderCell>
                              <SubTableHeaderCell>ค่าปกติ</SubTableHeaderCell>
                            </tr>
                          </thead>
                          <tbody>
                            {allNormalRanges.map((range, idx) => (
                              <SubTableRow key={idx}>
                                <SubTableCell>{range.sex}</SubTableCell>
                                <SubTableCell>{range.age_range}</SubTableCell>
                                <SubTableCell>{range.normal_range}</SubTableCell>
                              </SubTableRow>
                            ))}
                          </tbody>
                        </SubTable>
                      </div>
                    ) : (
                      <div style={{ padding: '0.25rem' }}>{normalValue}</div>
                    )}
                  </ParameterCell>
                  <ParameterCell>{setValue}</ParameterCell>
                </ParameterRow>
              );
            })}
          </ParameterGrid>
        </ParameterSection>

        <Divider />

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <SaveButton onClick={onClose}>
            ปิดหน้าต่าง
          </SaveButton>
        </div>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default TemplateDetailModal;
